import { Injectable } from '@angular/core';
import { BehaviorSubject, map, Observable } from 'rxjs';
import { VendorService } from '../../services/vendor.service';
import { TunnelDataStore } from '../tunnel/tunnel-data.store';
import { CustomerService } from '../../services/customer.service';
import { CalendarEvent } from '../../models/minutpass/full-calendar-model';
import { Customer } from '../../services/kingfisher/customer';
import { logError } from 'src/app/services/helpers/sentry.helper';

export type CustomerInfoState = {
  isOpen?: boolean;
  appointment?: CalendarEvent;
  needLogoutOnClose?: boolean;
  isLite?: boolean;
};

const customerInfoInitialState: CustomerInfoState = {
  isOpen: false,
  appointment: undefined,
  needLogoutOnClose: false,
  isLite: false,
};

@Injectable({
  providedIn: 'root',
})
export class CustomerInfoStore {
  private readonly customerInfoState$ = new BehaviorSubject<CustomerInfoState>(customerInfoInitialState);

  constructor(
    private vendorService: VendorService,
    private tunnelDataStore: TunnelDataStore,
    private customerService: CustomerService
  ) {}

  public getIsOpen(): Observable<boolean> {
    return this.customerInfoState$.pipe(map(data => data.isOpen));
  }

  public setIsOpen(isOpen: boolean) {
    if (!isOpen) {
      this.resetState();
    } else {
      this.updateState({
        isOpen: isOpen,
      });
    }
  }

  public getNeedLogoutOnClose(): Observable<boolean> {
    return this.customerInfoState$.pipe(map(data => data.needLogoutOnClose));
  }

  public setNeedLogoutOnClose(needLogoutOnClose: boolean) {
    this.updateState({
      needLogoutOnClose: needLogoutOnClose,
    });
  }

  public setPartialValue(value: CustomerInfoState) {
    this.updateState({ ...value });
  }

  public getAppointment(): Observable<CalendarEvent> {
    return this.customerInfoState$.pipe(map(data => data.appointment));
  }

  public setAppointment(appointment: CalendarEvent) {
    this.updateState({
      appointment: appointment,
    });
  }

  public getIsLite(): Observable<boolean> {
    return this.customerInfoState$.pipe(map(data => data.isLite));
  }

  public setIsLite(isLite: boolean) {
    this.updateState({
      isLite: isLite,
    });
  }

  public async openCustomerInfoTab(customerEmail: string) {
    if (!customerEmail) {
      this.openLiteCustomerInfoTab();
      return;
    }
    try {
      //On cherche le customer
      const customer = (
        await this.vendorService.searchCustomers({
          first: 0,
          email: customerEmail,
        })
      )?.customers[0];

      if (customer) {
        await this.openLoadedCustomerInfoTab(customer);
      } else {
        this.openLiteCustomerInfoTab();
      }
    } catch (err) {
      logError(err);
    }
  }

  private async openLoadedCustomerInfoTab(customer: Customer) {
    //on le charge dans le store pour l'affichage, et dans customerService au cas où (à voir si on peut le retirer)
    await this.tunnelDataStore.loadCurrentCustomer(customer);
    this.customerService.userDatas = customer;
    //on ouvre customer info
    this.setIsOpen(true);
  }

  private openLiteCustomerInfoTab() {
    //on indique au customerInfoTab de s'ouvrir en mode "lite"
    this.updateState({
      isLite: true,
    });
    //on ouvre customer info
    this.setIsOpen(true);
  }

  public resetState() {
    this.customerInfoState$.next(customerInfoInitialState);
  }

  private updateState(partialState: Partial<CustomerInfoState>): void {
    const currentState = this.customerInfoState$.getValue();
    this.customerInfoState$.next({
      ...currentState,
      ...partialState,
    });
  }
}
